import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from "react-router-dom";

/* ─── DESIGN TOKENS ─────────────────────────────────────────────────── */
const T = {
  bg:      '#0a0c10',
  bg2:     '#0e1117',
  bg3:     '#13171f',
  surface: '#1a1f2b',
  border:  'rgba(255,255,255,.07)',
  borderHi:'rgba(255,255,255,.12)',
  white:   '#e8edf5',
  muted:   'rgba(232,237,245,.45)',
  dim:     'rgba(232,237,245,.22)',
  green:   '#3dd68c',
  red:     '#ff5f57',
  yellow:  '#fbbf24',
  blue:    '#4d9fff',
  purple:  '#b78cff',
  orange:  '#ff9f43',
  cyan:    '#00d4ff',
  mono:    '"JetBrains Mono","Fira Code","Cascadia Code",monospace',
  sans:    '"DM Sans","Outfit","system-ui",sans-serif',
};

/* ─── SHARED ATOMS ───────────────────────────────────────────────────── */
function Pill({ children, active, onClick, color = T.blue }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 16px',
      borderRadius: 6,
      border: `1px solid ${active ? color + '60' : T.border}`,
      background: active ? color + '18' : 'transparent',
      color: active ? color : T.muted,
      fontSize: 12.5,
      fontWeight: 600,
      fontFamily: T.sans,
      cursor: 'pointer',
      letterSpacing: '.03em',
      transition: 'all .15s',
    }}>{children}</button>
  );
}

function StatusBadge({ ok, label }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px',
      borderRadius: 5,
      background: ok ? T.green + '18' : T.red + '18',
      border: `1px solid ${ok ? T.green + '40' : T.red + '40'}`,
      fontSize: 11.5,
      fontWeight: 700,
      color: ok ? T.green : T.red,
      letterSpacing: '.06em',
      textTransform: 'uppercase',
      fontFamily: T.sans,
    }}>
      <span style={{ fontSize: 9 }}>{ok ? '●' : '●'}</span>
      {label}
    </div>
  );
}

function IconBtn({ title, onClick, children, danger }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      title={title}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '5px 10px',
        borderRadius: 5,
        border: `1px solid ${hover ? (danger ? T.red + '50' : T.borderHi) : T.border}`,
        background: hover ? (danger ? T.red + '12' : T.surface) : 'transparent',
        color: hover ? (danger ? T.red : T.white) : T.muted,
        fontSize: 11.5,
        fontFamily: T.sans,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all .12s',
        display: 'flex', alignItems: 'center', gap: 5,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

function Label({ children }) {
  return (
    <div style={{
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: '.1em',
      textTransform: 'uppercase',
      color: T.dim,
      fontFamily: T.sans,
      marginBottom: 6,
    }}>{children}</div>
  );
}

const EDITOR_STYLE = {
  width: '100%',
  minHeight: 320,
  padding: '14px 16px',
  background: T.bg,
  border: `1px solid ${T.border}`,
  borderRadius: 8,
  color: T.white,
  fontSize: 12.5,
  fontFamily: T.mono,
  lineHeight: 1.65,
  resize: 'vertical',
  outline: 'none',
  tabSize: 2,
  transition: 'border-color .15s',
};

/* ─── JSON SYNTAX HIGHLIGHTER ────────────────────────────────────────── */
function highlight(json) {
  return json
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, match => {
      let cls = 'number';
      if (/^"/.test(match)) cls = /:$/.test(match) ? 'key' : 'string';
      else if (/true|false/.test(match)) cls = 'bool';
      else if (/null/.test(match)) cls = 'null';
      const colors = {
        key:    T.blue,
        string: T.green,
        number: T.orange,
        bool:   T.purple,
        null:   T.red,
      };
      return `<span style="color:${colors[cls]}">${match}</span>`;
    });
}

/* ─── 1. JSON FORMATTER ──────────────────────────────────────────────── */
function JSONTool() {
  const [input,    setInput]    = useState('');
  const [output,   setOutput]   = useState('');
  const [status,   setStatus]   = useState(null);   // null | {ok, msg}
  const [indent,   setIndent]   = useState(2);
  const [mode,     setMode]     = useState('format'); // format | minify | validate
  const [copied,   setCopied]   = useState(false);
  const [stats,    setStats]    = useState(null);
  const inputRef = useRef(null);

  const SAMPLE = `{
  "name": "Arhatinfo",
  "type": "AI Engineering Agency",
  "services": ["Django", "FastAPI", "OpenAI", "AWS"],
  "founded": 2021,
  "active": true,
  "contact": {
    "email": "hello@arhatinfo.com",
    "website": "arhatinfo.com"
  }
}`;

  const process = useCallback(() => {
    const raw = input.trim();
    if (!raw) { setStatus(null); setOutput(''); setStats(null); return; }

    try {
      const parsed = JSON.parse(raw);

      if (mode === 'validate') {
        setStatus({ ok: true, msg: 'Valid JSON ✓' });
        setOutput(JSON.stringify(parsed, null, indent));
        countStats(parsed);
        return;
      }
      if (mode === 'minify') {
        const min = JSON.stringify(parsed);
        setOutput(min);
        setStatus({ ok: true, msg: `Minified — ${raw.length} → ${min.length} chars` });
        countStats(parsed);
        return;
      }
      // format
      const formatted = JSON.stringify(parsed, null, indent);
      setOutput(formatted);
      setStatus({ ok: true, msg: `Valid JSON — ${formatted.split('\n').length} lines` });
      countStats(parsed);

    } catch (e) {
      setStatus({ ok: false, msg: e.message });
      setOutput('');
      setStats(null);
    }
  }, [input, mode, indent]);

  const countStats = (obj) => {
    let keys = 0, arrays = 0, depth = 0;
    const walk = (v, d = 0) => {
      if (d > depth) depth = d;
      if (Array.isArray(v)) { arrays++; v.forEach(x => walk(x, d+1)); }
      else if (v && typeof v === 'object') { Object.values(v).forEach(x => { keys++; walk(x, d+1); }); }
    };
    walk(obj);
    setStats({ keys, arrays, depth });
  };

  useEffect(() => { if (input) process(); }, [mode, indent]);

  const copy = () => {
    navigator.clipboard?.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const loadSample = () => { setInput(SAMPLE); setTimeout(process, 50); };

  // Tab support in textarea
  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const el  = e.target;
      const s   = el.selectionStart;
      const end = el.selectionEnd;
      const val = el.value;
      const spaces = ' '.repeat(indent);
      setInput(val.substring(0, s) + spaces + val.substring(end));
      setTimeout(() => { el.selectionStart = el.selectionEnd = s + indent; }, 0);
    }
  };

  return (
    <div>
      {/* toolbar */}
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14, flexWrap:'wrap' }}>
        <div style={{ display:'flex', gap:4 }}>
          {['format','minify','validate'].map(m => (
            <Pill key={m} active={mode===m} onClick={()=>setMode(m)}>
              {m.charAt(0).toUpperCase()+m.slice(1)}
            </Pill>
          ))}
        </div>
        <div style={{ display:'flex', gap:4, marginLeft:'auto', alignItems:'center' }}>
          <span style={{ fontSize:11.5, color:T.muted, marginRight:2 }}>Indent</span>
          {[2,4].map(n => (
            <Pill key={n} active={indent===n} onClick={()=>setIndent(n)} color={T.cyan}>
              {n}
            </Pill>
          ))}
        </div>
      </div>

      {/* editors */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
            <Label>Input</Label>
            <div style={{ display:'flex', gap:5 }}>
              <IconBtn onClick={loadSample}>Sample</IconBtn>
              <IconBtn onClick={()=>{ setInput(''); setOutput(''); setStatus(null); setStats(null); }} danger>
                Clear
              </IconBtn>
            </div>
          </div>
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onInput={process}
            placeholder={`Paste JSON here…\n\n{\n  "key": "value"\n}`}
            spellCheck={false}
            style={{
              ...EDITOR_STYLE,
              borderColor: status ? (status.ok ? T.green+'40' : T.red+'50') : T.border,
            }}
          />
          {/* status */}
          <div style={{ marginTop:8, display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            {status && <StatusBadge ok={status.ok} label={status.ok ? 'Valid' : 'Invalid'}/>}
            {status && (
              <span style={{ fontSize:11.5, color: status.ok ? T.muted : T.red, fontFamily:T.mono }}>
                {status.msg}
              </span>
            )}
          </div>
          {/* stats */}
          {stats && (
            <div style={{
              marginTop:8, display:'flex', gap:10, flexWrap:'wrap',
            }}>
              {[
                ['Keys',   stats.keys],
                ['Arrays', stats.arrays],
                ['Depth',  stats.depth],
                ['Chars',  input.length],
              ].map(([label, val]) => (
                <div key={label} style={{
                  padding:'3px 9px',
                  background:T.surface,
                  border:`1px solid ${T.border}`,
                  borderRadius:5,
                  fontSize:11,
                  color:T.muted,
                  fontFamily:T.mono,
                }}>
                  <span style={{ color:T.dim }}>{label} </span>
                  <span style={{ color:T.cyan }}>{val}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
            <Label>Output</Label>
            {output && (
              <IconBtn onClick={copy}>
                {copied ? '✓ Copied' : '⧉ Copy'}
              </IconBtn>
            )}
          </div>
          <div style={{
            ...EDITOR_STYLE,
            minHeight: 320,
            overflow: 'auto',
            whiteSpace: 'pre',
            cursor: 'text',
            userSelect: 'text',
            borderColor: output ? T.green+'30' : T.border,
          }}>
            {output
              ? <div dangerouslySetInnerHTML={{ __html: highlight(output) }}/>
              : <span style={{ color:T.dim }}>Formatted output will appear here…</span>
            }
          </div>
          {output && mode === 'format' && (
            <div style={{ marginTop:8, display:'flex', gap:5 }}>
              <IconBtn onClick={() => setInput(output)}>
                ↩ Use as input
              </IconBtn>
            </div>
          )}
        </div>
      </div>

      {/* keyboard hint */}
      <div style={{ marginTop:10, fontSize:11, color:T.dim }}>
        Tip: JSON is validated in real-time as you type · Tab key inserts spaces
      </div>
    </div>
  );
}

/* ─── 2. TEXT DIFF ───────────────────────────────────────────────────── */

function computeDiff(a, b) {
  const linesA = a.split('\n');
  const linesB = b.split('\n');

  // Myers diff algorithm (simplified LCS-based)
  const n = linesA.length;
  const m = linesB.length;

  // LCS DP
  const dp = Array.from({ length: n+1 }, () => new Array(m+1).fill(0));
  for (let i = 1; i <= n; i++)
    for (let j = 1; j <= m; j++)
      dp[i][j] = linesA[i-1] === linesB[j-1]
        ? dp[i-1][j-1] + 1
        : Math.max(dp[i-1][j], dp[i][j-1]);

  // Backtrack
  const result = [];
  let i = n, j = m;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && linesA[i-1] === linesB[j-1]) {
      result.unshift({ type:'same',    text:linesA[i-1], lineA:i, lineB:j });
      i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j-1] >= dp[i-1][j])) {
      result.unshift({ type:'added',   text:linesB[j-1], lineB:j });
      j--;
    } else {
      result.unshift({ type:'removed', text:linesA[i-1], lineA:i });
      i--;
    }
  }
  return result;
}

function inlineDiff(a, b) {
  // Character-level diff for changed lines
  const wordsA = a.split(/(\s+)/);
  const wordsB = b.split(/(\s+)/);
  const n = wordsA.length, m = wordsB.length;
  const dp = Array.from({ length:n+1 }, () => new Array(m+1).fill(0));
  for (let i=1;i<=n;i++)
    for (let j=1;j<=m;j++)
      dp[i][j] = wordsA[i-1]===wordsB[j-1] ? dp[i-1][j-1]+1 : Math.max(dp[i-1][j],dp[i][j-1]);
  const res = [];
  let i=n,j=m;
  while(i>0||j>0){
    if(i>0&&j>0&&wordsA[i-1]===wordsB[j-1]){ res.unshift({t:'same',v:wordsA[i-1]}); i--;j--; }
    else if(j>0&&(i===0||dp[i][j-1]>=dp[i-1][j])){ res.unshift({t:'add',v:wordsB[j-1]}); j--; }
    else { res.unshift({t:'del',v:wordsA[i-1]}); i--; }
  }
  return res;
}

function DiffLine({ item, showInline, lineNumWidth }) {
  const colors = {
    same:    { bg:'transparent', border:'transparent', num: T.dim, text:T.muted },
    added:   { bg:'rgba(61,214,140,.07)', border:T.green+'40', num:T.green+'80', text:T.green },
    removed: { bg:'rgba(255,95,87,.07)',  border:T.red+'40',   num:T.red+'80',   text:T.red  },
  };
  const c   = colors[item.type];
  const sym = item.type==='added' ? '+' : item.type==='removed' ? '−' : ' ';

  return (
    <div style={{
      display:'flex',
      background:c.bg,
      borderLeft:`2px solid ${c.border}`,
      minHeight:22,
    }}>
      {/* line numbers */}
      <div style={{
        display:'flex', flexShrink:0,
        borderRight:`1px solid ${T.border}`,
        userSelect:'none',
      }}>
        <div style={{
          width:lineNumWidth, textAlign:'right',
          padding:'1px 8px', fontSize:11,
          fontFamily:T.mono, color:c.num,
        }}>
          {item.type!=='added' ? (item.lineA||'') : ''}
        </div>
        <div style={{
          width:lineNumWidth, textAlign:'right',
          padding:'1px 8px', fontSize:11,
          fontFamily:T.mono, color:c.num,
          borderLeft:`1px solid ${T.border}`,
        }}>
          {item.type!=='removed' ? (item.lineB||'') : ''}
        </div>
      </div>
      {/* symbol */}
      <div style={{
        width:22, textAlign:'center', flexShrink:0,
        fontSize:12, fontFamily:T.mono, color:c.text,
        padding:'1px 0',
      }}>{sym}</div>
      {/* content */}
      <div style={{
        flex:1, padding:'1px 6px',
        fontSize:12.5, fontFamily:T.mono,
        color: item.type==='same' ? T.muted : c.text,
        whiteSpace:'pre-wrap', wordBreak:'break-all',
      }}>
        {showInline && item.type!=='same' && item._pair
          ? item._inlineSegs?.map((seg,i) => (
              <span key={i} style={{
                background: seg.t==='add'
                  ? T.green+'30'
                  : seg.t==='del'
                  ? T.red+'30'
                  : 'transparent',
                borderRadius:2,
              }}>{seg.v}</span>
            ))
          : item.text
        }
      </div>
    </div>
  );
}

function DiffTool() {
  const [left,    setLeft]    = useState('');
  const [right,   setRight]   = useState('');
  const [diff,    setDiff]    = useState(null);
  const [inline,  setInline]  = useState(true);
  const [context, setContext] = useState(3);   // lines of context around changes
  const [stats,   setStats]   = useState(null);

  const SAMPLE_A = `function greet(name) {
  console.log("Hello, " + name);
  return name;
}

const users = ["Alice", "Bob"];
users.forEach(greet);
`;

  const SAMPLE_B = `function greet(name, greeting = "Hello") {
  console.log(greeting + ", " + name + "!");
  return { name, greeting };
}

const users = ["Alice", "Bob", "Charlie"];
users.forEach(u => greet(u));
`;

  const runDiff = useCallback(() => {
    if (!left && !right) { setDiff(null); setStats(null); return; }
    const raw = computeDiff(left, right);

    // Pair removed/added for inline diff
    const paired = [];
    let i = 0;
    while (i < raw.length) {
      if (raw[i].type==='removed' && raw[i+1]?.type==='added') {
        const segsR = inlineDiff(raw[i].text, raw[i+1].text).map(s =>
          s.t==='add' ? { ...s, t:'same' } : s
        );
        const segsA = inlineDiff(raw[i].text, raw[i+1].text).map(s =>
          s.t==='del' ? { ...s, t:'same' } : s
        );
        paired.push({ ...raw[i],   _pair:true, _inlineSegs:segsR });
        paired.push({ ...raw[i+1], _pair:true, _inlineSegs:segsA });
        i += 2;
      } else {
        paired.push(raw[i]);
        i++;
      }
    }

    // Compute stats
    const added   = paired.filter(l=>l.type==='added').length;
    const removed = paired.filter(l=>l.type==='removed').length;
    const same    = paired.filter(l=>l.type==='same').length;
    setStats({ added, removed, same, total: paired.length });

    // Apply context windowing — hide same lines far from changes
    if (context >= 0) {
      const changed = new Set();
      paired.forEach((l, idx) => { if (l.type!=='same') changed.add(idx); });
      const visible = new Set();
      changed.forEach(idx => {
        for (let k=Math.max(0,idx-context); k<=Math.min(paired.length-1,idx+context); k++)
          visible.add(k);
      });
      const windowed = [];
      let   prevHidden = false;
      paired.forEach((l, idx) => {
        if (visible.has(idx)) {
          if (prevHidden) windowed.push({ type:'ellipsis' });
          windowed.push(l);
          prevHidden = false;
        } else {
          prevHidden = true;
        }
      });
      setDiff(windowed);
    } else {
      setDiff(paired);
    }
  }, [left, right, context, inline]);

  useEffect(() => { runDiff(); }, [left, right, context]);

  const loadSample = () => { setLeft(SAMPLE_A); setRight(SAMPLE_B); };

  const lineNumWidth = 38;

  return (
    <div>
      {/* toolbar */}
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14, flexWrap:'wrap' }}>
        <div style={{ display:'flex', gap:5 }}>
          <IconBtn onClick={loadSample}>Load Sample</IconBtn>
          <IconBtn
            onClick={()=>{ setLeft(''); setRight(''); setDiff(null); setStats(null); }}
            danger
          >
            Clear
          </IconBtn>
        </div>
        <div style={{ display:'flex', gap:4, alignItems:'center', marginLeft:'auto' }}>
          <span style={{ fontSize:11.5, color:T.muted }}>Context</span>
          {[3, 5, -1].map(n => (
            <Pill key={n} active={context===n} onClick={()=>setContext(n)} color={T.cyan}>
              {n === -1 ? 'Full' : `±${n}`}
            </Pill>
          ))}
        </div>
      </div>

      {/* stats bar */}
      {stats && (stats.added > 0 || stats.removed > 0) && (
        <div style={{
          display:'flex', gap:10, alignItems:'center',
          marginBottom:12, padding:'8px 12px',
          background:T.surface, borderRadius:7,
          border:`1px solid ${T.border}`,
          flexWrap:'wrap',
        }}>
          <span style={{ fontSize:11.5, color:T.dim, fontFamily:T.sans }}>Diff summary:</span>
          <span style={{ fontSize:12, color:T.green, fontFamily:T.mono, fontWeight:600 }}>
            +{stats.added} added
          </span>
          <span style={{ fontSize:12, color:T.red, fontFamily:T.mono, fontWeight:600 }}>
            −{stats.removed} removed
          </span>
          <span style={{ fontSize:12, color:T.muted, fontFamily:T.mono }}>
            {stats.same} unchanged
          </span>
          {stats.added === 0 && stats.removed === 0 && (
            <StatusBadge ok={true} label="Identical"/>
          )}
          {/* visual bar */}
          {stats.added + stats.removed > 0 && (
            <div style={{
              flex:1, minWidth:80, height:5, borderRadius:3,
              background:T.border, overflow:'hidden', marginLeft:4,
            }}>
              <div style={{
                height:'100%',
                width:`${(stats.added/(stats.added+stats.removed))*100}%`,
                background:`linear-gradient(90deg,${T.green},${T.green}80)`,
                borderRadius:3,
              }}/>
            </div>
          )}
        </div>
      )}

      {/* editors row */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
        {[
          { val:left,  set:setLeft,  label:'Original', placeholder:'Paste original text here…' },
          { val:right, set:setRight, label:'Modified',  placeholder:'Paste modified text here…' },
        ].map(({ val, set, label, placeholder }) => (
          <div key={label}>
            <Label>{label}</Label>
            <textarea
              value={val}
              onChange={e => set(e.target.value)}
              placeholder={placeholder}
              spellCheck={false}
              style={{ ...EDITOR_STYLE, minHeight:160, resize:'vertical' }}
            />
          </div>
        ))}
      </div>

      {/* diff output */}
      {diff && diff.length > 0 && (
        <div>
          <Label>Diff</Label>
          <div style={{
            border:`1px solid ${T.border}`,
            borderRadius:8,
            overflow:'auto',
            background:T.bg,
            maxHeight:520,
          }}>
            {diff.map((item, i) => (
              item.type === 'ellipsis'
                ? (
                  <div key={i} style={{
                    padding:'3px 16px',
                    fontSize:11, fontFamily:T.mono,
                    color:T.dim, background:T.bg2,
                    borderTop:`1px solid ${T.border}`,
                    borderBottom:`1px solid ${T.border}`,
                    userSelect:'none',
                  }}>
                    ⋯ unchanged lines hidden
                  </div>
                )
                : (
                  <DiffLine
                    key={i}
                    item={item}
                    showInline={inline}
                    lineNumWidth={lineNumWidth}
                  />
                )
            ))}
          </div>
        </div>
      )}

      {/* empty state */}
      {(!diff || diff.length === 0) && (left || right) && stats?.added === 0 && stats?.removed === 0 && (
        <div style={{
          padding:'32px', textAlign:'center',
          border:`1px dashed ${T.border}`, borderRadius:8,
        }}>
          <div style={{ fontSize:28, marginBottom:8 }}>✓</div>
          <div style={{ fontSize:14, fontWeight:600, color:T.green, marginBottom:4 }}>
            Files are identical
          </div>
          <div style={{ fontSize:12, color:T.dim }}>No differences found</div>
        </div>
      )}

      {!left && !right && (
        <div style={{
          padding:'32px', textAlign:'center',
          border:`1px dashed ${T.border}`, borderRadius:8,
        }}>
          <div style={{ fontSize:11.5, color:T.dim, lineHeight:1.8 }}>
            Paste text into both boxes above to see the diff<br/>
            Supports code, JSON, plain text, config files, anything
          </div>
        </div>
      )}

      <div style={{ marginTop:10, fontSize:11, color:T.dim }}>
        Tip: Diff updates live as you type · Context controls how many unchanged lines are shown around changes
      </div>
    </div>
  );
}

/* ─── MAIN PAGE ──────────────────────────────────────────────────────── */
const TOOLS = [
  { id:'json', label:'JSON Formatter', icon:'{ }', subtitle:'Format · Validate · Minify' },
  { id:'diff', label:'Text Diff',      icon:'±',   subtitle:'Compare any two texts'      },
];

export default function DevTools() {
      const navigate = useNavigate();
    
      const hostname = window.location.hostname;
    
    // app.dev.example.com => ["app", "dev", "example", "com"]
    const parts = hostname.split(".");
    
    const firstSubdomain =
      parts.length > 2 ? parts[0] : null;
    
    console.log(firstSubdomain);
    
    const path = window.location.pathname;
    
    // "/tools/useragent/"
    const match = path.match(/^\/tools\/dev\/([^/]+)/);
    
    const toolName = match ? match[1] : null;
    
    console.log(toolName);
    
      const [active, setActive] = useState(toolName?toolName:firstSubdomain ? firstSubdomain : 'json');
      

  return (
    <>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::placeholder { color: rgba(232,237,245,.2) !important; }
        textarea:focus { border-color: rgba(77,159,255,.4) !important; }
        textarea { transition: border-color .15s; }
        ::-webkit-scrollbar { width:6px; height:6px; }
        ::-webkit-scrollbar-track { background:transparent; }
        ::-webkit-scrollbar-thumb { background:rgba(255,255,255,.1); border-radius:3px; }
        ::-webkit-scrollbar-thumb:hover { background:rgba(255,255,255,.18); }
      `}</style>
 <div className="page">
    <section className="section" style={{ paddingTop: 0 }}>
      <div style={{
        minHeight: '100vh',
        background: T.bg,
        padding: '28px 0 48px',
        fontFamily: T.sans,
      }}>

        {/* ── HEADER ─────────────────────────────────────────────── */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
            <div style={{
              width:32, height:32, borderRadius:8,
              background:`linear-gradient(135deg,${T.blue}30,${T.purple}30)`,
              border:`1px solid ${T.blue}30`,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:15,
            }}>⌨</div>
            <h1 style={{
              fontSize:'clamp(1.4rem,3vw,1.9rem)',
              fontWeight:700,
              color:T.white,
              letterSpacing:'-.02em',
              fontFamily:T.sans,
            }}>
              Developer Tools
            </h1>
          </div>
          <p style={{ fontSize:13.5, color:T.muted, marginLeft:42 }}>
            JSON formatter, validator, text diff — all in your browser, nothing sent to any server
          </p>
        </div>

        {/* ── TABS ───────────────────────────────────────────────── */}
        <div style={{
          display:'flex', gap:6, marginBottom:20,
          borderBottom:`1px solid ${T.border}`, paddingBottom:0,
        }}>
          {TOOLS.map(t => (
            <button
              key={t.id}
              onClick={() => {navigate(`/tools/dev/${t.id}/`)
            setActive(t.id);
          }}
              style={{
                padding:'10px 18px',
                border:'none', borderBottom:`2px solid ${active===t.id ? T.blue : 'transparent'}`,
                background:'transparent',
                color: active===t.id ? T.white : T.muted,
                fontSize:13.5,
                fontWeight: active===t.id ? 600 : 400,
                fontFamily:T.sans,
                cursor:'pointer',
                display:'flex', alignItems:'center', gap:8,
                transition:'all .15s',
                marginBottom:-1,
              }}
            >
              <span style={{
                width:22, height:22, borderRadius:5,
                background: active===t.id ? T.blue+'20' : T.surface,
                display:'inline-flex', alignItems:'center', justifyContent:'center',
                fontSize:11, fontFamily:T.mono, fontWeight:700,
                color: active===t.id ? T.blue : T.dim,
              }}>{t.icon}</span>
              {t.label}
              <span style={{ fontSize:10.5, color:T.dim, fontWeight:400 }}>
                {t.subtitle}
              </span>
            </button>
          ))}
        </div>

        {/* ── ACTIVE TOOL ────────────────────────────────────────── */}
        <div style={{
          background:T.bg2,
          border:`1px solid ${T.border}`,
          borderRadius:12,
          padding:'20px 22px',
        }}>
          {active === 'json' && <JSONTool/>}
          {active === 'diff' && <DiffTool/>}
        </div>

        {/* ── FOOTER NOTE ────────────────────────────────────────── */}
        <div style={{
          marginTop:20, textAlign:'center',
          fontSize:11.5, color:T.dim,
        }}>
          🔒 All processing happens locally in your browser — no data is sent to any server
        </div>

      </div>
    </section>
  </div></>
  );
}